import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { MapPin, CheckCircle2, LocateFixed, Navigation, Square } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function trackerId() {
  let id = localStorage.getItem("ito_tracker_id");
  if (!id) { id = (crypto.randomUUID ? crypto.randomUUID() : String(Date.now()) + Math.random()); localStorage.setItem("ito_tracker_id", id); }
  return id;
}

export default function PublicLiveShare() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState("");
  const [sharing, setSharing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [lastSent, setLastSent] = useState(null);
  const [sentCount, setSentCount] = useState(0);
  const [stopped, setStopped] = useState(false);
  const posRef = useRef(null);
  const watchRef = useRef(null);
  const timerRef = useRef(null);

  const label = (info?.driver_name || "").trim() || "Driver";

  const loadInfo = useCallback(() => {
    axios.get(`${API}/p/track/${token}/geo`)
      .then((r) => { setInfo(r.data); if (r.data.stopped) setStopped(true); })
      .catch((e) => setErr(e.response?.data?.detail || "This tracking link is not valid."));
  }, [token]);

  useEffect(() => { loadInfo(); }, [loadInfo]);

  const stop = useCallback(() => {
    if (watchRef.current != null) navigator.geolocation.clearWatch(watchRef.current);
    if (timerRef.current) clearInterval(timerRef.current);
    watchRef.current = null; timerRef.current = null;
    setSharing(false);
  }, []);

  useEffect(() => () => stop(), [stop]);

  const sendPing = useCallback(async () => {
    if (!posRef.current) return;
    try {
      const { data } = await axios.post(`${API}/p/track/${token}/location`, {
        tracker_id: trackerId(), label,
        lat: posRef.current.lat, lng: posRef.current.lng, accuracy: posRef.current.accuracy,
      });
      setLastSent(new Date());
      setSentCount((c) => c + 1);
      if (data.stopped) { setStopped(true); stop(); }
    } catch (e) {
      if (process.env.NODE_ENV !== "production") console.debug("Live location ping failed, will retry", e?.message || e);
    }
  }, [token, label, stop]);

  // One tap: grab current spot NOW, send it, and (once) keep auto-updating in the background.
  function shareNow() {
    if (!navigator.geolocation) { setErr("Your phone does not support location."); return; }
    setErr("");
    setBusy(true);
    navigator.geolocation.getCurrentPosition(
      async (p) => {
        posRef.current = { lat: p.coords.latitude, lng: p.coords.longitude, accuracy: p.coords.accuracy };
        await sendPing();
        setBusy(false);
        if (!sharing) startAuto();
      },
      () => { setBusy(false); setErr("Could not get your location. Please tap Allow when your phone asks."); },
      { enableHighAccuracy: true, maximumAge: 0, timeout: 20000 },
    );
  }

  function startAuto() {
    setSharing(true);
    watchRef.current = navigator.geolocation.watchPosition(
      (p) => { posRef.current = { lat: p.coords.latitude, lng: p.coords.longitude, accuracy: p.coords.accuracy }; },
      () => {},
      { enableHighAccuracy: true, maximumAge: 5000, timeout: 20000 },
    );
    timerRef.current = setInterval(sendPing, 15000);
  }

  if (err && !info) return <div className="min-h-screen flex items-center justify-center bg-[#FDFCF8] text-[#8A2526] text-base p-6 text-center" data-testid="live-share-error">{err}</div>;
  if (!info) return <div className="min-h-screen flex items-center justify-center bg-[#FDFCF8] text-[#68665E] text-base">Loading…</div>;

  return (
    <div className="min-h-screen bg-[#FDFCF8]" data-testid="live-share-page">
      <div className="max-w-md mx-auto px-4 py-8">
        <div className="flex items-center gap-3 mb-6">
          <img src="/ito-logo.png" alt="ITO" className="w-10 h-10 object-contain" />
          <div>
            <div className="font-headings font-bold text-[#1A1A18] text-sm leading-tight">INDIAN TRANSPORT ORGANISATION</div>
            <div className="text-[10px] uppercase tracking-widest text-[#68665E]">Live Location</div>
          </div>
        </div>

        <div className="bg-white border border-[#E5E3DB] p-6 rounded-lg text-center">
          <div className="text-xs uppercase tracking-wide text-[#68665E]">Consignment</div>
          <div className="font-headings text-2xl font-bold text-[#1A1A18]" data-testid="live-share-lr">{info.lr_number}</div>
          {(info.from_location || info.to_location) && (
            <div className="text-sm text-[#68665E] mt-1 flex items-center justify-center gap-1"><MapPin className="w-3.5 h-3.5" /> {info.from_location || "—"} → {info.to_location || "—"}</div>
          )}
          {info.driver_name && <div className="text-sm text-[#1A1A18] mt-2 font-medium" data-testid="live-share-driver">{info.driver_name}{info.vehicle_number ? ` · ${info.vehicle_number}` : ""}</div>}

          {stopped ? (
            <div className="mt-6" data-testid="live-share-stopped">
              <CheckCircle2 className="w-12 h-12 text-[#2F6F4E] mx-auto mb-2" />
              <div className="font-medium text-[#1A1A18] text-lg">Delivery complete.</div>
              <div className="text-sm text-[#68665E]">Thank you!</div>
            </div>
          ) : (
            <>
              {/* One giant tap-to-share button */}
              <button onClick={shareNow} disabled={busy}
                className="mt-6 w-full rounded-2xl bg-[#2F6F4E] text-white py-8 flex flex-col items-center justify-center gap-2 active:scale-95 transition-transform disabled:opacity-60"
                data-testid="live-share-send">
                <Navigation className="w-12 h-12" />
                <span className="text-xl font-bold">{busy ? "Getting location…" : (sentCount === 0 ? "Share my location" : "Send my location again")}</span>
              </button>

              {sentCount > 0 && (
                <div className="mt-4 text-[#2F6F4E] flex items-center justify-center gap-2 text-base font-medium" data-testid="live-share-sent">
                  <CheckCircle2 className="w-5 h-5" /> Sent{lastSent ? ` at ${lastSent.toLocaleTimeString()}` : ""} · {sentCount} time{sentCount > 1 ? "s" : ""}
                </div>
              )}

              {sharing && (
                <div className="mt-2 text-[12px] text-[#68665E] flex items-center justify-center gap-2" data-testid="live-share-active">
                  <span className="relative flex h-2.5 w-2.5"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#2F6F4E] opacity-60" /><span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-[#2F6F4E]" /></span>
                  <LocateFixed className="w-3.5 h-3.5" /> Auto-updating in the background. Keep this page open.
                </div>
              )}

              {err && <div className="text-sm text-[#8A2526] mt-3" data-testid="live-share-inline-error">{err}</div>}

              {sharing && (
                <button onClick={stop} className="btn-ghost text-sm mt-4 mx-auto" data-testid="live-share-stop"><Square className="w-4 h-4" /> Stop sharing</button>
              )}
            </>
          )}
        </div>
        <p className="text-[11px] text-[#A09E96] text-center mt-4">Your location is shared with Indian Transport Organisation only for this delivery.</p>
      </div>
    </div>
  );
}
